import type { Document } from "../models"

/* eslint-disable max-params */
import { logger } from "@/lib/logger"
import { getCitationSourceForLookup } from "@/utils/citation-utils"

import { BACKEND_URI, fetchWithAuthRetry, getHeaders } from "./_shared"

const FILTH_MAX_ENTRY_ID = "9223372036854775807"
const FILTH_CONTENT_HANDLE = /^(?![.]{1,2}$)[A-Za-z0-9._-]{1,4096}$/
const FILTH_LIVE_CONTENT_SOURCE =
  /^[^:/?#]+:filth-(?:citation\/(?![.]{1,2}\/)[A-Za-z0-9._-]{1,4096}\/(?:preview[.]txt|original)|entry\/[1-9][0-9]{0,18}\/preview[.]txt)$/

function getCanonicalFilthContentHandle(value: unknown): string | null {
  return typeof value === "string" && FILTH_CONTENT_HANDLE.test(value) ? value : null
}

function getCanonicalFilthEntryId(value: unknown): string | null {
  if (typeof value === "number") {
    return Number.isSafeInteger(value) && value > 0 ? String(value) : null
  }
  if (typeof value !== "string" || !/^[1-9][0-9]{0,18}$/.test(value)) {
    return null
  }
  return value.length < FILTH_MAX_ENTRY_ID.length || value <= FILTH_MAX_ENTRY_ID ? value : null
}

function getFilthEntryIdFromSyntheticSource(source: string): string | null {
  const librarySeparator = source.indexOf(":")
  if (librarySeparator <= 0) {
    return null
  }
  const match = /^filth-entry-([1-9][0-9]{0,18})$/.exec(source.slice(librarySeparator + 1))
  return getCanonicalFilthEntryId(match?.[1])
}

/**
 * Build the FILTH content locator for a citation, taking the content handle only
 * from a document the backend delivered.
 *
 * The citation string is reproduced by the model, and a handle is hundreds of
 * opaque characters. Reading the handle back out of that string is
 * self-reinforcing: the one altered character that corrupts the handle is the
 * same one that breaks the exact-match lookup which would have supplied the real
 * handle, so the browser asks FILTH for a token FILTH never issued and the
 * citation can never open. The model is given a short alias instead, and the
 * alias resolves to a delivered document that carries the real handle in
 * `extra_data.filth_content_handle`.
 */
function getOpaqueFilthSource(source: string, document?: Document | null): string | null {
  const librarySeparator = source.indexOf(":")
  if (librarySeparator <= 0) {
    return null
  }
  const libraryId = source.slice(0, librarySeparator)
  const filthContentHandle = getCanonicalFilthContentHandle(
    document?.extra_data?.filth_content_handle
  )
  if (filthContentHandle) {
    return `${libraryId}:filth-citation/${filthContentHandle}/preview.txt`
  }
  const filthEntryId =
    getCanonicalFilthEntryId(document?.extra_data?.filth_entry_id) ??
    getFilthEntryIdFromSyntheticSource(source)
  return filthEntryId ? `${libraryId}:filth-entry/${filthEntryId}/preview.txt` : null
}

export function isOpaqueFilthCitation(citation: string, document?: Document | null): boolean {
  return getOpaqueFilthSource(getCitationSourceForLookup(citation), document) !== null
}

/**
 * True when a citation names a FILTH-backed library but no delivered document
 * answers to it — the model cited a source that is not in the evidence it was
 * given, or mangled the alias of one that is.
 *
 * Callers render this as an unresolvable citation. There is nothing to open: the
 * only trustworthy locator is the one carried by a delivered document.
 */
export function isUnresolvedFilthCitation(
  citation: string,
  document: Document | null | undefined,
  librariesAndDocuments: [string, Document][] | undefined
): boolean {
  const source = getCitationSourceForLookup(citation)
  if (getOpaqueFilthSource(source, document)) {
    return false
  }
  const librarySeparator = source.indexOf(":")
  if (librarySeparator <= 0) {
    return false
  }
  const libraryId = source.slice(0, librarySeparator)
  return Boolean(
    librariesAndDocuments?.some(
      ([deliveredLibraryId, delivered]) =>
        deliveredLibraryId === libraryId &&
        getCanonicalFilthContentHandle(delivered.extra_data?.filth_content_handle) !== null
    )
  )
}

/** True when a content URL must be reauthorized by FILTH on every viewer mount. */
export function isLiveAuthorizedFilthContentUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin)
    const match = /^\/api\/content\/[^/]+\/([^/]+)$/.exec(parsed.pathname)
    return match ? FILTH_LIVE_CONTENT_SOURCE.test(decodeURIComponent(match[1])) : false
  } catch {
    return false
  }
}

/**
 * UTILITY: Build the citation file path for content API and preview.
 *
 * Uses the citation source (without |page|quote) for the backend path.
 * Callers should append #page=N for client-side page navigation if needed.
 *
 * @param citation The citation identifier
 * @param section The section identifier
 * @returns The path to the citation file
 * @category Utility
 */
export function getCitationFilePath(
  citation: string,
  section: string,
  document?: Document | null
): string {
  const source = getCitationSourceForLookup(citation)
  const opaqueSource = getOpaqueFilthSource(source, document)
  if (opaqueSource) {
    return `${BACKEND_URI}/content/${section}/${encodeURIComponent(opaqueSource)}`
  }
  const encodedCitation = encodeURIComponent(source)
  if (source.startsWith("doc:")) {
    return `${BACKEND_URI}/content/doc/${encodedCitation}`
  }
  return `${BACKEND_URI}/content/${section}/${encodedCitation}`
}

/** Build the live-authorized original-artifact URL for a Context v1 citation. */
export function getCitationOriginalFilePath(
  citation: string,
  section: string,
  document?: Document | null
): string | null {
  const source = getCitationSourceForLookup(citation)
  const librarySeparator = source.indexOf(":")
  if (librarySeparator <= 0) {
    return null
  }
  const libraryId = source.slice(0, librarySeparator)
  // Delivered document only: a handle read back out of model-authored citation
  // text is a token FILTH never issued. See getOpaqueFilthSource.
  const handle = getCanonicalFilthContentHandle(document?.extra_data?.filth_content_handle)
  if (!handle) {
    return null
  }
  const originalSource = `${libraryId}:filth-citation/${handle}/original`
  return `${BACKEND_URI}/content/${section}/${encodeURIComponent(originalSource)}`
}

/** Mirror of Python _safe_blob_segment: take the basename and replace \\/#? with _. */
function safeBlobSegment(name: string): string {
  const base = name.replace(/\\/g, "/").split("/").pop() ?? name
  return base.replace(/[\\/#?]/g, "_") || "_"
}

/**
 * Build URL for the pre-conversion original blob at `originals/{indexed}/{original}`.
 * Requires the same citation `section` (session type) as getCitationFilePath.
 */
export function getLibraryOriginalFilePath(
  citationSection: string,
  libraryId: string,
  indexedBasename: string,
  originalBasename: string
): string {
  const blobPath = `originals/${safeBlobSegment(indexedBasename)}/${safeBlobSegment(originalBasename)}`
  const source = `${libraryId}:${blobPath}`
  return `${BACKEND_URI}/content/${citationSection}/${encodeURIComponent(source)}`
}

/**
 * QUERY: Downloads a citation file with proper authentication headers
 * @param url The full citation URL path
 * @param idToken The authentication token
 * @returns A Promise that resolves to a Blob containing the file data
 * @category Query
 */
export async function downloadCitationFileApi(
  url: string,
  idToken: string | undefined
): Promise<Blob> {
  try {
    logger.debug(`Downloading citation file: ${url}`)

    const response = await fetchWithAuthRetry(
      url,
      {
        method: "GET",
        headers: getHeaders(idToken),
        credentials: "include",
      },
      idToken
    )

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.status} ${response.statusText}`)
    }

    return await response.blob()
  } catch (error) {
    logger.error("Error downloading citation file", error as Error)
    throw error
  }
}

/**
 * QUERY: Fetches citation file content with proper authentication headers
 * @param url The full citation URL path
 * @param idToken The authentication token
 * @returns A Promise that resolves to an ArrayBuffer containing the file data
 * @category Query
 */
export async function fetchCitationFileContentApi(
  url: string,
  idToken: string | undefined
): Promise<ArrayBuffer> {
  try {
    logger.debug(`Fetching file content: ${url}`)

    const response = await fetchWithAuthRetry(
      url,
      {
        method: "GET",
        headers: getHeaders(idToken),
        credentials: "include",
      },
      idToken
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch file content: ${response.status} ${response.statusText}`)
    }

    return await response.arrayBuffer()
  } catch (error) {
    logger.error("Error fetching file content", error as Error)
    throw error
  }
}

/**
 * QUERY: Fetches file content (generic) with proper authentication headers
 * @param url The full URL path
 * @param idToken The authentication token
 * @returns A Promise that resolves to an ArrayBuffer containing the file data
 * @category Query
 */
export async function fetchFileContentApi(
  url: string,
  idToken: string | undefined
): Promise<ArrayBuffer> {
  if (url.startsWith("blob:") || url.startsWith("data:")) {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(
        `Failed to fetch local file content: ${response.status} ${response.statusText}`
      )
    }
    return await response.arrayBuffer()
  }
  return fetchCitationFileContentApi(url, idToken)
}

/**
 * MUTATION: Downloads a response in a specific format
 * @param idToken - Authentication token
 * @param responseText - Text content to download
 * @param inputFormat - Input format (e.g., "markdown")
 * @param outputFormat - Output format (e.g., "docx", "pdf")
 * @param outputFilename - Desired filename
 * @param useTemplate - Whether to use a template
 * @param sessionId - Optional session ID for resolving session images
 * @returns Response with download blob
 * @category Mutation
 */
export async function downloadResponseApi(
  idToken: string | undefined,
  responseText: string,
  inputFormat: string,
  outputFormat: string,
  outputFilename: string,
  useTemplate: boolean = false,
  sessionId?: string
): Promise<Response> {
  let url = `${BACKEND_URI}/download-response?inputFormat=${inputFormat}&outputFormat=${outputFormat}&outputFilename=${outputFilename}&useTemplate=${useTemplate}`
  if (sessionId) {
    url += `&sessionId=${encodeURIComponent(sessionId)}`
  }
  return fetchWithAuthRetry(
    url,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: responseText,
    },
    idToken
  )
}
